import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Play, AlertTriangle, Check, Radio, Beaker, BarChart3, BookOpen,
  Shield, Download, GraduationCap,
} from 'lucide-react';
import { API } from '@/App';
import { ExportBtn } from './PhenoScanSubTab';
import ValidationPanel from '@/components/ValidationPanel';

const HANDOFF_TARGETS = [
  ['Path Analysis', '/biometrical-analysis', 'path_analysis'],
  ['PCA', '/biometrical-analysis', 'pca'],
  ['D² & Cluster', '/biometrical-analysis', 'd_squared'],
  ['Heritability', '/biometrical-analysis', 'heritability'],
  ['Selection Index', '/biometrical-analysis', 'selection_index'],
  ['AMMI', '/biometrical-analysis', 'ammi'],
  ['GGE Biplot', '/biometrical-analysis', 'gge_biplot'],
];

export default function NIR() {
  const [mode, setMode] = useState('predict');   // 'predict' | 'train'
  return (
    <div className="space-y-4" data-testid="nir-pane">
      <div className="flex gap-2" role="tablist">
        <TabBtn active={mode === 'predict'} onClick={() => setMode('predict')}
          icon={Radio} label="Predict (bundled models)" id="nir-tab-predict" />
        <TabBtn active={mode === 'train'} onClick={() => setMode('train')}
          icon={GraduationCap} label="Train your own model" id="nir-tab-train" />
      </div>
      {mode === 'predict' ? <PredictPane /> : <TrainPane />}
      <ValidationPanel subTab="nir" subTabLabel="NIR Prediction" />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, id }) {
  return (
    <button onClick={onClick} data-testid={id}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
      style={{ background: active ? '#1B4332' : '#fff', color: active ? '#fff' : '#1B4332',
               border: '1px solid ' + (active ? '#1B4332' : '#E5E5E0'), fontWeight: 600 }}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ────────────────── PREDICT PANE ──────────────────
function PredictPane() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [trait, setTrait] = useState('moisture');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [modelOverride, setModelOverride] = useState('');
  const [bundled, setBundled] = useState([]);
  const [userModels, setUserModels] = useState([]);
  const [ackOOD, setAckOOD] = useState(false);   // user acknowledged out-of-domain

  useEffect(() => {
    axios.get(`${API}/phenoscan/nir/models`).then(r => {
      setBundled(r.data.bundled_models || []);
      setUserModels(r.data.user_models || []);
    }).catch(() => {});
  }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setResult(null); setAckOOD(false);
    try {
      const text = await f.text();
      const rows = text.split('\n').slice(0, 5).join('\n');
      setPreview(rows);
    } catch { setPreview(null); }
  };

  const loadSample = async (ref) => {
    setBusy(true);
    try {
      const [csvR, metaR] = await Promise.all([
        axios.get(`${API}/phenoscan/nir/sample/${ref}`, { responseType: 'blob' }),
        axios.get(`${API}/phenoscan/nir/sample-meta/${ref}`),
      ]);
      const f = new File([csvR.data], metaR.data.filename || `nir_sample_${ref}.csv`,
        { type: 'text/csv' });
      setFile(f); setResult(null); setAckOOD(false);
      const text = await csvR.data.text();
      setPreview(text.split('\n').slice(0, 5).join('\n'));
      toast.success(`Loaded sample #${ref} — lab values: ${JSON.stringify(metaR.data.lab_reference_values)}`);
    } catch { toast.error('Failed to load sample'); }
    setBusy(false);
  };

  const run = async () => {
    if (!file) return toast.error('Upload or load a spectrum first');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('trait', trait);
      if (modelOverride) fd.append('model_id', modelOverride);
      const r = await axios.post(`${API}/phenoscan/nir/predict`, fd);
      setResult(r.data.results);
      toast.success('Prediction complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Prediction failed');
    } finally { setBusy(false); }
  };

  const exportAs = async (fmt) => {
    if (!result) return;
    try {
      const r = await axios.post(`${API}/phenoscan/export?fmt=${fmt}`,
        { analysis: 'nir', results: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `phenoscan_nir.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const hasOOD = useMemo(() =>
    result?.predictions?.some(p => p.is_out_of_domain), [result]);

  const traitMatrix = useMemo(() => {
    if (!result?.predictions) return null;
    const perSample = {};
    result.predictions.forEach(p => {
      if (!perSample[p.sample_id]) perSample[p.sample_id] = {};
      perSample[p.sample_id][`nir_${result.trait}`] = p.predicted_value;
      perSample[p.sample_id][`nir_${result.trait}_ood`] = p.is_out_of_domain;
    });
    return { source: 'phenoscan_nir', trait: result.trait, per_sample: perSample };
  }, [result]);

  const handoff = (path, target) => {
    if (!traitMatrix) return;
    navigate(path, { state: { phenoscanHandoff: { ...traitMatrix, target } } });
    toast.success('NIR trait matrix handed off to ' + target);
  };

  // OOD warning banner — blocks results until acknowledged
  const userModelsForTrait = userModels.filter(m =>
    m.trait.toLowerCase() === trait.toLowerCase());
  const bundledForTrait = bundled.filter(m =>
    m.trait.toLowerCase() === trait.toLowerCase());

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
      {/* Left panel */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>1. Spectrum</h3>
        <div onClick={() => fileRef.current?.click()} data-testid="nir-upload-zone"
          className="rounded-xl p-4 mb-3 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}>
          {file ? (
            <div>
              <div className="text-sm" style={{ color: '#1B4332', fontWeight: 600 }}>{file.name}</div>
              {preview && <pre className="text-[10px] mt-2 text-left bg-white p-2 rounded border"
                style={{ maxHeight: 80, overflow: 'auto', color: '#4F4B44' }}>{preview}</pre>}
            </div>
          ) : (
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" /> Upload a CSV / XLSX spectrum
            </div>
          )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt,.tsv" className="hidden"
            onChange={onPickFile} data-testid="nir-upload-input" />
        </div>

        <div className="text-xs mb-2" style={{ color: '#4F4B44' }}>Load a validated Eigenvector Corn reference:</div>
        <div className="grid grid-cols-4 gap-1 mb-5">
          {[1, 2, 3, 4].map(ref => (
            <button key={ref} onClick={() => loadSample(ref)} disabled={busy}
              className="rounded-lg py-1.5 text-xs"
              style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
              data-testid={`nir-load-sample-${ref}`}>
              Sample {ref}
            </button>
          ))}
        </div>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>2. Trait</h3>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {['moisture', 'oil', 'protein', 'starch'].map(t => (
            <button key={t} onClick={() => { setTrait(t); setModelOverride(''); setAckOOD(false); }}
              data-testid={`nir-trait-${t}`}
              className="rounded-md px-3 py-1.5 text-sm"
              style={{ background: trait === t ? '#1B4332' : '#fff',
                color: trait === t ? '#fff' : '#1B4332',
                border: `1px solid ${trait === t ? '#1B4332' : '#E5E5E0'}`, fontWeight: 600 }}>
              {t}
            </button>
          ))}
          {['fiber', 'phenolics'].map(t => (
            <button key={t} disabled data-testid={`nir-trait-${t}`}
              title="No bundled model available — train your own in the Train tab"
              className="rounded-md px-3 py-1.5 text-sm opacity-50 cursor-not-allowed"
              style={{ background: '#F3F6F3', color: '#6F695F', border: '1px dashed #E5E5E0', fontWeight: 600 }}>
              {t} <AlertTriangle size={10} className="inline ml-1" />
            </button>
          ))}
        </div>

        {/* Bundled model picker */}
        {bundledForTrait.length > 0 && (
          <div className="mb-4">
            <label className="text-xs mb-1 block" style={{ color: '#6F695F' }}>Model (override auto-select)</label>
            <select value={modelOverride} onChange={e => { setModelOverride(e.target.value); setAckOOD(false); }}
              data-testid="nir-model-select"
              className="w-full rounded px-3 py-1.5 text-sm bg-white"
              style={{ border: '1px solid #E5E5E0' }}>
              <option value="">Auto-select</option>
              {bundledForTrait.map(m => (
                <option key={m.model_id} value={m.model_id}>
                  {m.species_common} · {m.training_dataset} · R²={m.metrics?.test?.r2}
                </option>
              ))}
              {userModelsForTrait.map(m => (
                <option key={m.model_id} value={m.model_id}>
                  USER · {m.species_common} · n={m.n_train} · R²_cal={m.metrics?.calibration?.r2}
                </option>
              ))}
            </select>
          </div>
        )}

        <button onClick={run} disabled={busy || !file} data-testid="nir-run-btn"
          className="w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
          style={{ background: '#1B4332', color: '#fff' }}>
          <Play size={14} className="inline mr-2" /> {busy ? 'Predicting…' : 'Predict'}
        </button>
      </div>

      {/* Right panel */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!result ? (
          <div className="text-center py-16">
            <Radio size={32} className="mx-auto mb-3" style={{ color: '#BCC5B5' }} />
            <div className="font-display text-lg" style={{ color: '#6F695F' }}>No predictions yet</div>
            <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
              Upload a spectrum or load a validated sample to get started.
            </div>
          </div>
        ) : (
          <div data-testid="nir-results">
            {/* Model banner */}
            <div className="rounded-xl p-4 mb-4" data-testid="nir-model-banner"
              style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <div className="text-xs" style={{ color: '#1E40AF' }}>Model used</div>
              <div className="text-sm font-semibold mt-1" style={{ color: '#1E3A8A' }}>
                {result.model_used.species_common} ({result.model_used.species_scientific}) ·
                &nbsp;{result.model_used.training_dataset}
              </div>
              <div className="text-xs mt-1" style={{ color: '#3730A3' }}>
                {result.model_used.selection_reason} · trained on λ {result.model_used.wavelength_range[0]}–{result.model_used.wavelength_range[1]} nm ·
                R²_test = {result.model_used.metrics?.test?.r2},  RPD = {result.model_used.metrics?.test?.rpd}
              </div>
            </div>

            {/* Out-of-domain species warning */}
            {hasOOD && !ackOOD && (
              <div className="rounded-xl p-4 mb-4" data-testid="nir-ood-banner"
                style={{ background: '#FEF3C7', border: '2px solid #F59E0B' }}>
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} style={{ color: '#B45309', flexShrink: 0 }} />
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#78350F' }}>
                      Out-of-domain prediction — bundled model is trained on CORN kernels.
                    </div>
                    <div className="text-xs mt-1" style={{ color: '#92400E' }}>
                      Eigenvector Cargill Corn, 80 samples. If your samples are NOT corn,
                      predictions are biased. Continue only for exploratory use — or switch to
                      the <b>Train your own model</b> tab and calibrate on matched species.
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setAckOOD(true)} data-testid="nir-ood-ack"
                        className="px-3 py-1.5 rounded-md text-xs font-semibold"
                        style={{ background: '#F59E0B', color: '#fff' }}>
                        Continue — predictions flagged "out-of-domain"
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(!hasOOD || ackOOD) && (
              <>
                {/* Action bar */}
                <div className="flex flex-wrap gap-2 items-center mb-4">
                  <span data-testid="nir-summary-badge" className="px-2.5 py-1 rounded-full text-xs"
                    style={{ background: '#F3F6F3', color: '#1B4332' }}>
                    {result.summary.n_samples} sample{result.summary.n_samples !== 1 ? 's' : ''} ·
                    mean = {result.summary.mean} · SD = {result.summary.sd} ·
                    {result.summary.n_out_of_domain} OOD ({result.summary.pct_out_of_domain}%)
                  </span>
                  <div className="ml-auto flex gap-1">
                    <ExportBtn onClick={() => exportAs('csv')} prefix="nir">CSV</ExportBtn>
                    <ExportBtn onClick={() => exportAs('xlsx')} prefix="nir">XLSX</ExportBtn>
                    <ExportBtn onClick={() => exportAs('pdf')} prefix="nir">PDF</ExportBtn>
                  </div>
                </div>

                {/* Per-sample table */}
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
                  <table className="w-full text-sm" data-testid="nir-pred-table">
                    <thead>
                      <tr style={{ background: '#F3F6F3', color: '#4F4B44', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <th className="text-left px-3 py-2">Sample</th>
                        <th className="text-right px-3 py-2">Predicted {result.trait}</th>
                        <th className="text-right px-3 py-2">Mahalanobis</th>
                        <th className="text-right px-3 py-2">Cutoff</th>
                        <th className="text-right px-3 py-2">OOD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.predictions.map(p => (
                        <tr key={p.sample_id} style={{ borderTop: '1px solid #F0EFEA' }}>
                          <td className="px-3 py-2" style={{ color: '#1B4332' }}>{p.sample_id}</td>
                          <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums', color: '#1B4332' }}>{p.predicted_value}</td>
                          <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums', color: '#4F4B44' }}>{p.mahalanobis_distance}</td>
                          <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums', color: '#6F695F' }}>{p.mahalanobis_95_cutoff}</td>
                          <td className="px-3 py-2 text-right">
                            {p.is_out_of_domain ?
                              <span className="px-2 py-0.5 rounded text-[10px]" style={{ background: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}>OUT-OF-DOMAIN</span> :
                              <Check size={12} style={{ color: '#065F46', display: 'inline' }} />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Model method card */}
                <div className="rounded-xl p-4 mt-5" data-testid="nir-method"
                  style={{ border: '1px solid #E5E5E0', background: '#FAFBF9' }}>
                  <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#4F4B44' }}>
                    <BookOpen size={11} className="inline mr-1" /> Method &amp; traceability
                  </div>
                  <div className="text-xs" style={{ color: '#4F4B44' }}>
                    <div><span style={{ color: '#6F695F' }}>Training citation:</span>{' '}
                      <b style={{ color: '#1B4332' }}>{result.model_used.citation}</b></div>
                    <div><span style={{ color: '#6F695F' }}>R²_cal:</span>{' '}
                      {result.model_used.metrics?.calibration?.r2} ·
                      <span style={{ color: '#6F695F' }}> RMSE_cal:</span>{' '}
                      {result.model_used.metrics?.calibration?.rmse} ·
                      <span style={{ color: '#6F695F' }}> R²_test:</span>{' '}
                      {result.model_used.metrics?.test?.r2} ·
                      <span style={{ color: '#6F695F' }}> RMSE_test:</span>{' '}
                      {result.model_used.metrics?.test?.rmse} ·
                      <span style={{ color: '#6F695F' }}> RPD:</span>{' '}
                      {result.model_used.metrics?.test?.rpd}</div>
                  </div>
                  {/* Honest-context disclaimer — prevents over-interpretation of R²_test */}
                  <div className="text-xs rounded-md px-3 py-2 mt-3"
                    data-testid="nir-r2-disclaimer"
                    style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A8A' }}>
                    <b>Interpretation:</b> Reported R²_test is the <u>held-out benchmark</u>{' '}
                    performance on the <i>{result.model_used.training_dataset}</i> dataset (same instrument,
                    same day, same lot). Field performance on <u>your</u> samples depends on similarity
                    to this calibration — see the <code>is_out_of_domain</code> flag per prediction
                    for spectrum-level assessment.
                  </div>
                  {/* Expected-field-range estimate */}
                  {result.model_used.validation_protocol?.expected_field_range?.expected_field_r2 && (
                    <div className="text-xs rounded-md px-3 py-2 mt-2"
                      data-testid="nir-field-range"
                      style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#78350F' }}>
                      <b>Expected field range (estimate):</b>{' '}
                      R² <b>{result.model_used.validation_protocol.expected_field_range.expected_field_r2}</b>,{' '}
                      RPD <b>{result.model_used.validation_protocol.expected_field_range.expected_field_rpd}</b>{' '}
                      for {result.model_used.species_common.toLowerCase()} samples on similar instruments.{' '}
                      <span style={{ color: '#92400E' }}>
                        {result.model_used.validation_protocol.expected_field_range.note}
                      </span>{' '}
                      <i>Your mileage will vary based on sample similarity to the calibration set.</i>
                    </div>
                  )}
                </div>

                {/* Hand-off */}
                <div className="rounded-xl p-4 mt-4"
                  style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}
                  data-testid="nir-handoff">
                  <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#6F695F' }}>
                    Next step — hand off NIR trait matrix to BKAgriD statistics
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {HANDOFF_TARGETS.map(([label, path, key]) => (
                      <button key={key} onClick={() => handoff(path, key)}
                        data-testid={`nir-handoff-${label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ background: '#fff', border: '1px solid #2D6A4F', color: '#1B4332' }}>
                        {label} →
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────── TRAIN PANE ──────────────────
function TrainPane() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [refCol, setRefCol] = useState('');
  const [traitName, setTraitName] = useState('protein');
  const [species, setSpecies] = useState('');
  const [refMethod, setRefMethod] = useState('Kjeldahl');
  const [maxLV, setMaxLV] = useState(10);
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setRecord(null);
  };

  const train = async () => {
    if (!file) return toast.error('Upload a calibration CSV/XLSX first');
    if (!refCol) return toast.error('Tell us which column holds the reference lab values');
    if (!species) return toast.error('Please supply the species / sample type');
    setBusy(true); setRecord(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('reference_col', refCol);
      fd.append('trait_name', traitName);
      fd.append('species', species);
      fd.append('reference_method', refMethod);
      fd.append('max_lv', String(maxLV));
      const r = await axios.post(`${API}/phenoscan/nir/train`, fd);
      setRecord(r.data.model);
      toast.success(`Trained user model ${r.data.model.model_id.slice(-8)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Training failed');
    } finally { setBusy(false); }
  };

  const downloadJSON = () => {
    if (!record) return;
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${record.model_id}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>Calibration CSV / XLSX</h3>
        <div onClick={() => fileRef.current?.click()} data-testid="nir-train-upload-zone"
          className="rounded-xl p-4 mb-4 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}>
          {file ? <div className="text-sm" style={{ color: '#1B4332', fontWeight: 600 }}>{file.name}</div> :
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" /> Upload calibration set
            </div>}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt,.tsv"
            className="hidden" onChange={onPickFile}
            data-testid="nir-train-upload-input" />
        </div>

        <div className="text-xs rounded-md px-3 py-2 mb-3"
          style={{ background: '#FFFBEB', color: '#78350F', border: '1px solid #FDE68A' }}>
          <AlertTriangle size={10} className="inline mr-1" />
          Minimum 30 samples. 30–50 ⇒ warning. Reference-value CV &lt; 10 % ⇒ warning.
        </div>

        <FormRow label="Reference column name">
          <input value={refCol} onChange={e => setRefCol(e.target.value)}
            placeholder="e.g. protein_pct, ndf_pct"
            data-testid="nir-train-refcol"
            className="w-full rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0' }} />
        </FormRow>
        <FormRow label="Trait name">
          <input value={traitName} onChange={e => setTraitName(e.target.value)}
            data-testid="nir-train-trait"
            className="w-full rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0' }} />
        </FormRow>
        <FormRow label="Species / sample type">
          <input value={species} onChange={e => setSpecies(e.target.value)}
            placeholder="Wheat / Soybean / Apple leaf / …"
            data-testid="nir-train-species"
            className="w-full rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0' }} />
        </FormRow>
        <FormRow label="Reference method">
          <input value={refMethod} onChange={e => setRefMethod(e.target.value)}
            placeholder="Kjeldahl / LECO combustion / AOAC 925.10 / …"
            className="w-full rounded px-3 py-1.5 text-sm"
            style={{ border: '1px solid #E5E5E0' }} />
        </FormRow>
        <FormRow label={`Max latent variables: ${maxLV}`}>
          <input type="range" min={3} max={20} value={maxLV}
            onChange={e => setMaxLV(parseInt(e.target.value, 10))}
            data-testid="nir-train-maxlv"
            className="w-full" />
        </FormRow>

        <button onClick={train} disabled={busy || !file}
          className="w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 mt-2"
          style={{ background: '#1B4332', color: '#fff' }}
          data-testid="nir-train-submit">
          <Beaker size={14} className="inline mr-2" /> {busy ? 'Training…' : 'Train PLSR'}
        </button>
      </div>

      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!record ? (
          <div className="text-center py-16">
            <GraduationCap size={32} className="mx-auto mb-3" style={{ color: '#BCC5B5' }} />
            <div className="font-display text-lg" style={{ color: '#6F695F' }}>No model trained yet</div>
            <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
              Train a custom model for fiber, phenolics, or any other trait with your own reference data.
            </div>
          </div>
        ) : (
          <div data-testid="nir-train-results">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={16} style={{ color: '#065F46' }} />
              <div className="font-display text-lg" style={{ color: '#1B4332' }}>{record.model_id}</div>
              <button onClick={downloadJSON} data-testid="nir-train-download"
                className="ml-auto px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46' }}>
                <Download size={11} /> Download model JSON
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="n samples" value={record.n_train} />
              <Stat label="best LV" value={record.pls.n_lv} />
              <Stat label="R²_cal" value={record.metrics.calibration.r2} />
              <Stat label="RMSE_cal" value={record.metrics.calibration.rmse} />
              <Stat label="R²_cv (approx)"
                value={record.metrics.cv?.rmse ?
                  (1 - Math.pow(record.metrics.cv.rmse, 2) / Math.pow(record.calibration_summary.y_sd, 2)).toFixed(4) : '—'} />
              <Stat label="RMSE_cv" value={record.metrics.cv?.rmse} />
              <Stat label="RPD_cv" value={record.metrics.cv?.rpd} />
              <Stat label="Mahalanobis 95%" value={record.ood?.mahalanobis_95_cutoff?.toFixed(3)} />
            </div>

            {record.warnings && record.warnings.length > 0 && (
              <div className="rounded-xl p-3 mb-4"
                style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}
                data-testid="nir-train-warnings">
                <div className="text-xs font-semibold mb-1" style={{ color: '#78350F' }}>
                  <AlertTriangle size={11} className="inline mr-1" /> Training warnings
                </div>
                <ul className="text-xs list-disc pl-5" style={{ color: '#92400E' }}>
                  {record.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Diagnostic plots */}
            <Diagnostic title="Cross-validation scree (RMSECV vs LV)"
              testid="nir-scree">
              <SceneCV d={record.diagnostics.cv_scree} />
            </Diagnostic>
            <Diagnostic title="Predicted vs Actual (calibration)"
              testid="nir-predvsactual">
              <PredVsActual d={record.diagnostics.pred_vs_actual} />
            </Diagnostic>
            <Diagnostic title="PLS scores (T1 vs T2) — cluster / outlier check"
              testid="nir-scores">
              <ScoresPlot d={record.diagnostics.pls_scores} />
            </Diagnostic>
            <Diagnostic title="Residuals vs Predicted" testid="nir-resid">
              <ResidPlot d={record.diagnostics.residuals} />
            </Diagnostic>
            <Diagnostic title="Reference-value histogram" testid="nir-hist">
              <HistPlot d={record.diagnostics.y_histogram} />
            </Diagnostic>
          </div>
        )}
      </div>
    </div>
  );
}

// ───── helpers ─────
function FormRow({ label, children }) {
  return (
    <label className="block mb-3 text-sm">
      <div className="mb-1" style={{ color: '#4F4B44' }}>{label}</div>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md p-2" style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}>
      <div className="text-[10px]" style={{ color: '#6F695F', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div className="text-sm" style={{ color: '#1B4332', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</div>
    </div>
  );
}

function Diagnostic({ title, testid, children }) {
  return (
    <div className="rounded-xl p-4 mb-3" data-testid={testid}
      style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}>
      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#4F4B44' }}>
        <BarChart3 size={11} className="inline mr-1" /> {title}
      </div>
      {children}
    </div>
  );
}

function SceneCV({ d }) {
  const max = Math.max(...d.rmsecv);
  return (
    <svg viewBox="0 0 300 80" className="w-full" style={{ maxHeight: 100 }}>
      {d.lv.map((lv, i) => {
        const x = 20 + (i * 260 / (d.lv.length - 1));
        const y = 70 - (d.rmsecv[i] / max) * 55;
        const next = i < d.lv.length - 1 ? (() => {
          const x2 = 20 + ((i + 1) * 260 / (d.lv.length - 1));
          const y2 = 70 - (d.rmsecv[i + 1] / max) * 55;
          return <line x1={x} y1={y} x2={x2} y2={y2} stroke="#2D6A4F" strokeWidth="1" />;
        })() : null;
        const best = lv === d.best_lv;
        return <g key={lv}>
          {next}
          <circle cx={x} cy={y} r={best ? 3 : 2} fill={best ? '#F59E0B' : '#2D6A4F'} />
          <text x={x} y={78} fontSize="6" textAnchor="middle" fill="#6F695F">{lv}</text>
        </g>;
      })}
    </svg>
  );
}

function PredVsActual({ d }) {
  const all = [...d.actual, ...d.predicted];
  const lo = Math.min(...all), hi = Math.max(...all);
  const norm = v => 10 + (v - lo) / (hi - lo + 1e-9) * 280;
  return (
    <svg viewBox="0 0 300 100" className="w-full" style={{ maxHeight: 120 }}>
      <line x1={norm(lo)} y1={90 - (0) * 80} x2={norm(hi)} y2={10} stroke="#BCC5B5" strokeDasharray="2 2" />
      {d.actual.map((a, i) => (
        <circle key={i} cx={norm(a)} cy={90 - (d.predicted[i] - lo) / (hi - lo + 1e-9) * 80}
          r={2} fill="#2D6A4F" fillOpacity={0.6} />
      ))}
    </svg>
  );
}

function ScoresPlot({ d }) {
  if (!d.t1?.length) return <div className="text-xs" style={{ color: '#BCC5B5' }}>single-LV model</div>;
  const xs = d.t1, ys = d.t2?.length ? d.t2 : xs.map(() => 0);
  const xlo = Math.min(...xs), xhi = Math.max(...xs);
  const ylo = Math.min(...ys), yhi = Math.max(...ys);
  const nx = v => 20 + ((v - xlo) / (xhi - xlo + 1e-9)) * 260;
  const ny = v => 90 - ((v - ylo) / (yhi - ylo + 1e-9)) * 70;
  return (
    <svg viewBox="0 0 300 100" className="w-full" style={{ maxHeight: 120 }}>
      {xs.map((_, i) => <circle key={i} cx={nx(xs[i])} cy={ny(ys[i])} r={2} fill="#2D6A4F" fillOpacity={0.55} />)}
    </svg>
  );
}

function ResidPlot({ d }) {
  const xs = d.predicted, ys = d.residual;
  const xlo = Math.min(...xs), xhi = Math.max(...xs);
  const ylo = Math.min(...ys), yhi = Math.max(...ys);
  const absY = Math.max(Math.abs(ylo), Math.abs(yhi));
  const nx = v => 20 + ((v - xlo) / (xhi - xlo + 1e-9)) * 260;
  const ny = v => 50 - (v / (absY + 1e-9)) * 40;
  return (
    <svg viewBox="0 0 300 100" className="w-full" style={{ maxHeight: 120 }}>
      <line x1={20} y1={50} x2={280} y2={50} stroke="#BCC5B5" strokeDasharray="2 2" />
      {xs.map((_, i) => <circle key={i} cx={nx(xs[i])} cy={ny(ys[i])} r={2} fill="#2D6A4F" fillOpacity={0.55} />)}
    </svg>
  );
}

function HistPlot({ d }) {
  const max = Math.max(...d.counts);
  return (
    <div className="flex items-end gap-1" style={{ height: 80 }}>
      {d.counts.map((c, i) => (
        <div key={i} className="flex-1" title={`${d.edges[i].toFixed(2)}–${d.edges[i + 1].toFixed(2)}: ${c}`}
          style={{ height: `${(c / max) * 70}px`, background: '#2D6A4F', borderRadius: '2px 2px 0 0' }} />
      ))}
    </div>
  );
}
